/* eslint-disable ember/classic-decorator-hooks */
import EmberObject, { set } from '@ember/object';

import * as CircuitUtils from '../utils/circuit';
import * as CommonUtils from '../../utils/common';
import * as ResistorToleranceUtils from '../tolerances/resistor-tolerance-utils';
import { computeNominalValue } from '../tolerances/tolerance-utils';

//
// The idea behind a these components (capacitor, resistor, inductor) is we have classes that encapsulate
// how each of them are created.
// R4 is a gain resistor. Meaning if the specs requires gain,
// r3 and r4 are added in the circuit. Not all circuits support gains.
// r3's nominal value is kept within the limits unlike in r4 where we
// compute for the closest nominalValue. The reason being, ideally,
// r4 will be within the limits if r3 is also within the limits.
//
// Lifecycle: This component is created with the
// 1. ideal value (component value based from computation, aka. textbook component in the old app, or ideal value),
// 2. a multiplier to get the minimum limit.
// 3. a multiplier to get the maximum limit.
//
// This is the component that changes when the gain slider is changed. This component is like c1 where it is ensured
// that the final result will be within the limits.
//
// Construction: R3.create({idealValue: idealValue, resistorMaximumLimitMultiplier: resistorMaximumLimitMultiplier, resistorMinimumLimitMultiplier: resistorMinimumLimitMultiplier})
// Hash:
// hash = {
//              idealValue: { number },
//              resistorMaximumLimitMultiplier: { number },
//              resistorMinimumLimitMultiplier: { number }
//        }
// Create: Capacitor.create(hash);
// @inputProperty { number } idealValue - The computed value based from a given c1.
// @inputProperty { number } resistorMaximumLimitMultiplier - A value computed from the ideal values of all the components. Used to compute this component's maximum limit.
// @inputProperty { number } resistorMinimumLimitMultiplier - A value computed from the ideal values of all the components used to compute this component's minimum limit.

// @getterProperty { number } nominalValue - The computed commercial value. This matches the value of capacitors that are available in the market.
// @getterProperty { number[] } toleranceArray - The nominal value multiplied to each of the elements of the array. The array consists of different tolerance multipliers.
// @getterProperty { FilterConstants.capacitorTolerance } finalTolerance - If the desired tolerance fails to get the nominal, there's a process that goes through all the available tolerance options to get the nominal value.
// @getterProperty { FilterConstants.capacitorTolerance } desiredTolerance - the selected capacitor tolerance or the default if none.
// @getterProperty { bool } isCommercial - True if this component is already converted into a commercial capacitor. False if not.

// @computed { number } maximum - the capacitor instance's maximum limit
// @computed { number } minimum - the capacitor instance's minimum limit
// @computed { number } value - the ideal value if the component is not commercial, the nominal value if the component is already converted to a commercial one.
// @method { this } toCommercialComponent() - converts the component into its commercial version.
// @method { number } getNominalValue() - gets the nominal value. Can only be accessed when the component is already a commercial one.
export default class R3 extends EmberObject {
  idealValue = null;
  resistorMaximumLimitMultiplier = null;
  resistorMinimumLimitMultiplier = null;
  nominalValue = null;
  toleranceArray = null;
  finalTolerance = null;
  isCommercial = null;
  definedValue = null;

  init() {
    super.init();
    set(this, 'isCommercial', false);

    this.minimum =
      CommonUtils.isNullOrUndefined(this.minimum) || this.minimum === 0
        ? CircuitUtils.computeGainResistorLimit(this.idealValue, this.resistorMinimumLimitMultiplier)
        : this.minimum;
    this.maximum =
      CommonUtils.isNullOrUndefined(this.maximum) || this.maximum === 0
        ? CircuitUtils.computeGainResistorLimit(this.idealValue, this.resistorMaximumLimitMultiplier)
        : this.maximum;
    this.value = this.idealValue;
  }

  toCommercialComponent(toleranceInput) {
    set(this, 'toleranceInput', toleranceInput);

    let tolInput = !toleranceInput ? CircuitUtils.createToleranceInputDefaults() : toleranceInput;
    // let nominalValue = ResistorToleranceUtils.computeNominalValue(this.idealValue, tolInput, 'closest');
    let nominalValue = computeNominalValue(this.idealValue, tolInput.resistorPreferredSeries, 'closest');
    var minimum = this.minimum;
    var maximum = this.maximum;

    if (nominalValue < minimum) {
      // nominalValue = ResistorToleranceUtils.computeNominalValue(this.idealValue, tolInput, 'up');
      nominalValue = computeNominalValue(this.idealValue, tolInput.resistorPreferredSeries, 'up');
    } else if (nominalValue > maximum) {
      // nominalValue = ResistorToleranceUtils.computeNominalValue(this.idealValue, tolInput, 'down');
      nominalValue = computeNominalValue(this.idealValue, tolInput.resistorPreferredSeries, 'down');
    }

    let toleranceValues = ResistorToleranceUtils.getToleranceValues(nominalValue, tolInput.resistorTolerance);
    set(this, 'value', nominalValue);
    set(this, 'toleranceArray', toleranceValues.toleranceArray);
    set(this, 'finalTolerance', toleranceValues.finalTolerance);
    set(this, 'desiredTolerance', tolInput.resistorTolerance);
    set(this, 'isCommercial', true);

    return this;
  }

  setValue(value) {
    if (value < this.minimum) set(this, 'idealValue', this.minimum);
    else if (value > this.maximum) set(this, 'idealValue', this.maximum);
    else set(this, 'idealValue', value);

    set(this, 'value', this.idealValue);
    return this;
  }

  getNominalValue() {
    if (!this.isCommercial) throw 'component is not yet commercial. Call toCommercialComponent() first.';

    return this.value;
  }
}
